import { useEffect, forwardRef, useImperativeHandle } from 'react';
import type { ReactNode } from 'react';
import { useEmblaCarousel } from './useEmblaCarousel';
import type { OptionsType } from 'embla-carousel/embla-carousel-vanilla/options';

import styled from 'styled-components';

const Embla = styled('div')`
  width: 100%;
  height: 100%;

  &:before {
    pointer-events: none;
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    z-index: 1;
    margin-left: -4px;
    width: 20px;
    background: linear-gradient(
      90deg,
      ${({ theme }) => theme.color.noir} 15.38%,
      ${({ theme }) => theme.color.noirRGBA(0)} 100%
    );
  }

  &:after {
    pointer-events: none;
    content: '';
    position: absolute;
    top: 0;
    left: 100%;
    right: 0;
    bottom: 0;
    z-index: 1;
    margin-left: 4px;
    width: 20px;
    transform: translateX(-100%);
    background: linear-gradient(
      270deg,
      ${({ theme }) => theme.color.noir} 15.38%,
      ${({ theme }) => theme.color.noirRGBA(0)} 100%
    );
  }

  .is-draggable {
    cursor: move;
    cursor: grab;
  }

  .is-dragging {
    cursor: grabbing;
  }
`;

// ======

interface CarouselProps extends Partial<OptionsType> {
  slidesToScroll?: number;
  totalSlides?: number;
  children: ReactNode;
  showGradient?: boolean;
  className?: string;
  onScroll?: () => void;
  onClickNext?: () => void;
  onClickPrev?: () => void;
}

export interface CarouselRef {
  selectedScrollSnap: () => number | undefined;
  scrollNext: () => void;
  scrollPrev: () => void;
  scrollTo: (index: number, jump?: boolean) => void;
  slidesInView: (target?: boolean) => number[] | undefined;
}

export const EmblaCarousel = forwardRef<CarouselRef, CarouselProps>(
  (
    {
      slidesToScroll = 1,
      totalSlides = 0,
      onScroll,
      onClickNext,
      onClickPrev,
      showGradient,
      children,
      ...props
    },
    ref
  ) => {
    const [emblaRef, embla] = useEmblaCarousel({
      slidesToScroll,
      ...props,
    });

    useEffect(() => {
      if (!embla) return;
      const handler = () => onScroll && onScroll();
      embla.on('select', handler);
      return () => {
        embla.off('select', handler);
      };
    }, [embla, onScroll]);

    useEffect(() => {
      if (!embla) return;
      if (embla.slideNodes().length !== totalSlides) {
        embla.reInit();
      }
    }, [embla, totalSlides]);

    useImperativeHandle(ref, () => ({
      selectedScrollSnap: () => embla?.selectedScrollSnap(),
      scrollNext: () => embla?.scrollNext(),
      scrollPrev: () => embla?.scrollPrev(),
      scrollTo: (index: number, jump?: boolean) => {
        embla?.scrollTo(index, jump);
      },
      slidesInView: (target = true) => embla?.slidesInView(target),
    }));

    return <Embla ref={emblaRef}>{children}</Embla>;
  }
);

EmblaCarousel.displayName = 'EmblaCarousel';

const Carousel = forwardRef<CarouselRef, CarouselProps>((props, ref) => {
  return <EmblaCarousel ref={ref} {...props} />;
});

Carousel.displayName = 'Carousel';

export { Carousel };
